import { useQuery } from '@apollo/client';
import { useCentralizedProviderData } from '@crehana/auth';

import type { IconEnum } from '@/generated/graphql/types.mock';

import type {
  GetOutOfOfficeDataQuery,
  GetOutOfOfficeDataQueryVariables,
} from './graphql/get-out-of-office-data.v5.mock.generated';
import GET_OUT_OF_OFFICE_DATA_QUERY from './graphql/get-out-of-office-data.v5.mock.graphql';

type TOutOfOfficeUser = {
  id: string;
  name: string;
  avatar_url?: string | null;
  job_title: string;
  policy_name: string;
  from: string;
  to: string;
  policy_type: TPolicyType;
};

type THolidayUser = {
  id: string;
  name: string;
  avatar_url?: string | null;
};

type THoliday = {
  id: string;
  name: string;
  date: string;
  applies_to_me: boolean;
  users: THolidayUser[];
};

type TPolicyType = {
  id: string;
  name: string;
  icon: IconEnum;
  created_at: string;
};

export const useOutOfOfficeData = () => {
  const { currentPageOrganization } = useCentralizedProviderData();
  const { data, loading, error, refetch } = useQuery<
    GetOutOfOfficeDataQuery,
    GetOutOfOfficeDataQueryVariables
  >(GET_OUT_OF_OFFICE_DATA_QUERY, {
    variables: {
      input: {
        centralized_organization_id:
          currentPageOrganization?.id.toString() ?? '',
      },
    },
    skip: !currentPageOrganization?.id,
    notifyOnNetworkStatusChange: true,
    context: { clientName: 'mock.v5' },
  });

  const outOfOfficeData = data?.time_off?.get_out_of_office_data;

  const transformOutOfOfficeUsers = (users: TOutOfOfficeUser[]) => {
    return users.map(user => ({
      id: user.id,
      name: user.name,
      avatarUrl: user.avatar_url || undefined,
      jobTitle: user.job_title || undefined,
      policyName: user.policy_name,
      policyType: user.policy_type,
      from: user.from,
      to: user.to,
    }));
  };

  const transformHolidays = (holidays: THoliday[]) => {
    return holidays.map(holiday => ({
      id: holiday.id,
      name: holiday.name,
      date: holiday.date,
      appliesToMe: holiday.applies_to_me,
      users: holiday.users.map(user => ({
        id: user.id,
        name: user.name,
        avatarUrl: user.avatar_url || undefined,
      })),
    }));
  };

  const outOfOfficeUsers =
    outOfOfficeData?.__typename === 'OutOfOfficeData' &&
    outOfOfficeData.out_of_office_users
      ? transformOutOfOfficeUsers(outOfOfficeData.out_of_office_users)
      : [];

  const holidays =
    outOfOfficeData?.__typename === 'OutOfOfficeData' &&
    outOfOfficeData.holidays
      ? transformHolidays(outOfOfficeData.holidays as THoliday[])
      : [];

  const upcomingOutOfOfficeUsers =
    outOfOfficeData?.__typename === 'OutOfOfficeData' &&
    outOfOfficeData.upcoming_out_of_office_users
      ? transformOutOfOfficeUsers(outOfOfficeData.upcoming_out_of_office_users)
      : [];

  const upcomingHolidays =
    outOfOfficeData?.__typename === 'OutOfOfficeData' &&
    outOfOfficeData.upcoming_holidays
      ? transformHolidays(outOfOfficeData.upcoming_holidays)
      : [];

  return {
    loading,
    error,
    refetch,
    outOfOfficeUsers,
    holidays,
    upcomingOutOfOfficeUsers,
    upcomingHolidays,
  };
};
